import { API_ENDPOINTS, DEFAULT_PAGE_SIZE, ENTITY_STATUS } from '@/constants';
import { apiRequest } from '@/services/client';
import { validateBeneficiaryResponse } from '@/services/shared/contractValidation';
import {
  downloadEndpointFile,
  uploadSingleFile,
} from '@/services/shared/fileTransfer';
import { mapApiListResponse } from '@/services/shared/listResponse';
import type { ApiListResponse } from '@/types/api';
import type {
  Beneficiary,
  BeneficiaryCreateRequest,
  BeneficiaryListParams,
  BeneficiaryListResult,
  BeneficiaryUpdateRequest,
} from '@/types/beneficiary';
import { normalizeUiPageFromParams } from '@/utils/pagination';

type BeneficiaryListApiResponse = unknown[] | ApiListResponse<unknown>;

const mapListResponse = (
  response: BeneficiaryListApiResponse,
  params: BeneficiaryListParams,
): BeneficiaryListResult => {
  return mapApiListResponse<unknown, Beneficiary>(response, params, {
    defaultPageSize: DEFAULT_PAGE_SIZE,
    mapItem: (item, index) =>
      validateBeneficiaryResponse(item, {
        operation: 'fetch_beneficiaries_list_item',
        endpoint: API_ENDPOINTS.BENEFICIARIES.LIST,
        itemIndex: index,
      }),
  });
};

const normalizeStatusParam = (status?: ENTITY_STATUS | ENTITY_STATUS[]) => {
  if (!status) return undefined;
  return Array.isArray(status) ? status.join(',') : status;
};

export const fetchBeneficiaries = async (
  params: BeneficiaryListParams = {},
): Promise<BeneficiaryListResult> => {
  const normalizedPage = normalizeUiPageFromParams(params);

  const requestParams: Record<string, unknown> = {
    page: normalizedPage,
    size: params.size,
    status: normalizeStatusParam(params.status),
  };

  if (params.text && params.text.trim() !== '') {
    requestParams.text = params.text;
  }

  if (params.segment && params.segment.trim() !== '') {
    requestParams.segment = params.segment.trim();
  }

  const response = await apiRequest<BeneficiaryListApiResponse>(
    API_ENDPOINTS.BENEFICIARIES.LIST,
    { params: requestParams, retry: { retries: 1 } },
  );
  return mapListResponse(response, params);
};

export const createBeneficiary = async (
  payload: BeneficiaryCreateRequest,
): Promise<Beneficiary> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.BENEFICIARIES.CREATE,
    {
      method: 'POST',
      data: payload,
      retry: { retries: 0 },
    },
  );
  return validateBeneficiaryResponse(response, {
    operation: 'create_beneficiary',
    endpoint: API_ENDPOINTS.BENEFICIARIES.CREATE,
  });
};

export const updateBeneficiary = async (
  payload: BeneficiaryUpdateRequest,
): Promise<Beneficiary> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.BENEFICIARIES.UPDATE,
    {
      method: 'PUT',
      data: payload,
      retry: { retries: 0 },
    },
  );
  return validateBeneficiaryResponse(response, {
    operation: 'update_beneficiary',
    endpoint: API_ENDPOINTS.BENEFICIARIES.UPDATE,
  });
};

export const deleteBeneficiary = async (id: number): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.BENEFICIARIES.DELETE(id), {
    method: 'DELETE',
    retry: { retries: 0 },
  });
};

export const bulkDeleteBeneficiaries = async (ids: number[]): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.BENEFICIARIES.BULK_DELETE, {
    method: 'DELETE',
    data: ids,
    retry: { retries: 0 },
  });
};

export const bulkReactivateBeneficiaries = async (
  ids: number[],
): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.BENEFICIARIES.BULK_REACTIVATE, {
    method: 'PATCH',
    data: ids,
    retry: { retries: 0 },
  });
};

export const reactivateBeneficiary = async (
  id: number,
): Promise<Beneficiary> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.BENEFICIARIES.REACTIVATE(id),
    {
      method: 'PATCH',
      retry: { retries: 0 },
    },
  );
  return validateBeneficiaryResponse(response, {
    operation: 'reactivate_beneficiary',
    endpoint: API_ENDPOINTS.BENEFICIARIES.REACTIVATE(id),
  });
};

export const downloadBeneficiaries = async (): Promise<{
  blob: Blob;
  filename: string;
}> => {
  return downloadEndpointFile(
    API_ENDPOINTS.BENEFICIARIES.EXPORT,
    'beneficiary.xlsx',
  );
};

export const uploadBeneficiariesFile = async (
  file: File,
): Promise<{ jobId?: string; message?: string }> => {
  return uploadSingleFile(API_ENDPOINTS.BENEFICIARIES.UPLOAD, file);
};
